import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import {
  listMembershipQueue,
  membershipStatusCounts,
  setMembershipStatus,
  getMemberStatusHistory,
} from "@/lib/admin.functions";
import { MEMBERSHIP_LABEL, type MembershipState, type MembershipStatus } from "@/lib/membership";
import { ACCESS_BLOCK_LABEL, isAccessBlockReason } from "@/lib/member-access";
import { formatCpf } from "@/lib/cpf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { CheckCircle2, History, RotateCcw, XCircle } from "lucide-react";
import { formatDateTimeBR } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/aprovacoes")({
  head: () => ({
    meta: [
      { title: "Aprovações de cadastro · Admin · Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Fila administrativa de cadastros em análise, aprovados e reprovados da área de membros.",
      },
      { property: "og:title", content: "Aprovações de cadastro · Admin" },
      {
        property: "og:description",
        content: "Aprove, reprove e reavalie cadastros da área de membros.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ApprovalsPage,
});

type Tab = MembershipStatus | "all";

const TABS: { key: Tab; label: string }[] = [
  { key: "pending", label: "Em análise" },
  { key: "approved", label: "Aprovados" },
  { key: "rejected", label: "Reprovados" },
  { key: "all", label: "Todos" },
];

function fmt(value?: string | null) {
  if (!value) return "—";
  return formatDateTimeBR(value);
}

function blockLabel(reason: unknown): string {
  return isAccessBlockReason(reason) ? ACCESS_BLOCK_LABEL[reason] : String(reason);
}

function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("pending");
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  const queueFn = useServerFn(listMembershipQueue);
  const countsFn = useServerFn(membershipStatusCounts);
  const statusFn = useServerFn(setMembershipStatus);
  const historyFn = useServerFn(getMemberStatusHistory);

  const { data: counts } = useQuery({
    queryKey: ["membership-counts"],
    queryFn: () => countsFn(),
    staleTime: 30_000,
  });

  const {
    data: rows,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["membership-queue", tab],
    queryFn: () => queueFn({ data: { status: tab } }),
    staleTime: 15_000,
  });

  const { data: history } = useQuery({
    queryKey: ["membership-history", historyFor],
    queryFn: () => historyFn({ data: { userId: historyFor! } }),
    enabled: !!historyFor,
  });

  const change = useMutation({
    mutationFn: (input: { userId: string; status: MembershipStatus; reason?: string }) =>
      statusFn({ data: input }),
    onSuccess: async (_r, vars) => {
      await queryClient.invalidateQueries({ queryKey: ["membership-queue"] });
      await queryClient.invalidateQueries({ queryKey: ["membership-counts"] });
      await queryClient.invalidateQueries({ queryKey: ["members"] });
      setReasons((prev) => ({ ...prev, [vars.userId]: "" }));
      toast.success(
        vars.status === "approved"
          ? "Cadastro aprovado."
          : vars.status === "rejected"
            ? "Cadastro reprovado. O registro foi mantido como histórico."
            : "Cadastro devolvido para análise.",
      );
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível concluir a ação."),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-primary">Aprovações de cadastro</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Estar autenticado não libera a área de membros: o acesso depende de um cadastro aprovado e
          ativo. Cadastros reprovados permanecem salvos como histórico e podem ser reavaliados.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const count =
            t.key === "all" ? counts?.all : (counts as any)?.[t.key as keyof typeof counts];
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                tab === t.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:text-primary",
              )}
            >
              {t.label}
              {/* Espaço fixo para o número: evita que os botões mudem de
                  largura (e quebrem de linha) quando a contagem carrega. */}
              <span className="ml-1 inline-block w-8 text-left tabular-nums">
                {typeof count === "number" ? `(${count})` : ""}
              </span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="min-h-[70vh] rounded-xl border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">Carregando cadastros...</p>
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "Falha ao carregar os cadastros."}
        </p>
      ) : (rows ?? []).length === 0 ? (
        <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          Nenhum cadastro nesta situação.
        </p>
      ) : (
        <ul className="space-y-4">
          {(rows ?? []).map((m: any) => (
            <li key={m.id} className="cv-auto rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-primary">{m.full_name ?? "Sem nome"}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {m.email ?? "sem e-mail"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    CPF: {m.cpf ? formatCpf(m.cpf) : "—"} · Cadastro: {fmt(m.created_at)}
                  </p>
                </div>
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-primary">
                  {MEMBERSHIP_LABEL[(m.membership_status as MembershipStatus) ?? "pending"]}
                </span>
              </div>

              <dl className="mt-3 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                {m.approved_at ? (
                  <div>
                    <dt className="inline font-medium">Aprovado em: </dt>
                    <dd className="inline">
                      {fmt(m.approved_at)}
                      {m.approved_by_name ? ` por ${m.approved_by_name}` : ""}
                    </dd>
                  </div>
                ) : null}
                {m.rejected_at ? (
                  <div>
                    <dt className="inline font-medium">Reprovado em: </dt>
                    <dd className="inline">
                      {fmt(m.rejected_at)}
                      {m.rejected_by_name ? ` por ${m.rejected_by_name}` : ""}
                    </dd>
                  </div>
                ) : null}
                {m.rejection_reason ? (
                  <div className="sm:col-span-2">
                    <dt className="inline font-medium">Motivo: </dt>
                    <dd className="inline">{m.rejection_reason}</dd>
                  </div>
                ) : null}
                {m.access_block_reason ? (
                  <div className="sm:col-span-2">
                    <dt className="inline font-medium">Bloqueio da Gestão: </dt>
                    <dd className="inline">
                      {blockLabel(m.access_block_reason)}

                      {m.access_block_detail ? ` — ${m.access_block_detail}` : ""}
                    </dd>
                  </div>
                ) : null}
              </dl>

              {m.membership_status !== "approved" ? (
                <div className="mt-4 space-y-2">
                  <Label htmlFor={`reason-${m.id}`} className="text-xs">
                    Motivo da reprovação (obrigatório para reprovar)
                  </Label>
                  <Input
                    id={`reason-${m.id}`}
                    value={reasons[m.id] ?? ""}
                    onChange={(e) => setReasons((p) => ({ ...p, [m.id]: e.target.value }))}
                    placeholder="Ex.: cadastro sem vínculo com a congregação"
                  />
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                {m.membership_status !== "approved" ? (
                  <Button
                    size="sm"
                    disabled={change.isPending}
                    onClick={() => change.mutate({ userId: m.id, status: "approved" })}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Aprovar
                  </Button>
                ) : null}
                {m.membership_status !== "rejected" ? (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={change.isPending}
                    onClick={() =>
                      change.mutate({
                        userId: m.id,
                        status: "rejected",
                        reason: reasons[m.id]?.trim() || undefined,
                      })
                    }
                  >
                    <XCircle className="mr-2 h-4 w-4" /> Reprovar
                  </Button>
                ) : null}
                {m.membership_status !== "pending" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={change.isPending}
                    onClick={() => change.mutate({ userId: m.id, status: "pending" })}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" /> Reavaliar (voltar para análise)
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setHistoryFor(historyFor === m.id ? null : m.id)}
                >
                  <History className="mr-2 h-4 w-4" />
                  {historyFor === m.id ? "Ocultar histórico" : "Histórico"}
                </Button>
              </div>

              {historyFor === m.id ? (
                <ul className="mt-3 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
                  {(history ?? []).length === 0 ? (
                    <li>Sem mudanças registradas.</li>
                  ) : (
                    (history ?? []).map((h: any) => (
                      <li key={h.id}>
                        {fmt(h.created_at)} —{" "}
                        {MEMBERSHIP_LABEL[(h.from_status ?? "none") as MembershipState] ??
                          h.from_status ??
                          "—"}{" "}
                        → {MEMBERSHIP_LABEL[h.to_status as MembershipState] ?? h.to_status} (
                        {h.source}){h.reason ? ` · ${h.reason}` : ""}
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
